import currencyCodes from "currency-codes";
import { countries as flagSupportedCountries } from "country-flag-icons";
import en from "react-phone-number-input/locale/en.json";

// Currency code -> { country (ISO 3166 alpha-2, for country-flag-icons —
// already a dependency, used by PhoneNumberField.jsx), countryName }.
// Computed once at module load (166 currencies as of writing — negligible
// cost) instead of a hand-maintained table, so every currency in the
// admin's Add-a-Currency picker (see currencyCatalog.js) gets a correct
// flag, not just a short hand-picked subset.
//
// ISO 4217 convention: a currency code's first 2 letters are usually the
// issuing country's own ISO 3166 alpha-2 code (USD->US, GBP->GB, KYD->KY,
// JPY->JP, ...) — this is far more reliable than matching currency-codes'
// verbose official country-name list against react-phone-number-input's
// locale names, which otherwise tends to grab whichever minor territory
// happens to sort first in that list (e.g. USD's own country list starts
// with "American Samoa", not "United States"). Codes starting with "X" are
// reserved by the standard for commodities/supranational/shared currencies
// with no single issuing country (XAU gold, XCD East Caribbean Dollar,
// bond-market units, ...), so those skip the prefix shortcut and fall back
// to name-matching (picking whichever one of several valid countries
// happens to match first — the same unavoidable ambiguity EUR would have
// if it weren't for the "EU" flag existing).
const flagSupported = new Set(flagSupportedCountries);

const normalize = (s) =>
  s
    .toLowerCase()
    .replace(/\(the\)/g, "")
    .replace(/\bthe\b/g, "")
    .replace(/[.,'()]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const iso2ByNormalizedName = {};
Object.entries(en).forEach(([iso2, name]) => {
  iso2ByNormalizedName[normalize(name)] = iso2;
});

const findIso2ForCountryName = (countryName) => {
  const norm = normalize(countryName);
  if (iso2ByNormalizedName[norm]) return iso2ByNormalizedName[norm];
  for (const [name, iso2] of Object.entries(iso2ByNormalizedName)) {
    if (norm.includes(name) || name.includes(norm)) return iso2;
  }
  return null;
};

const buildCurrencyCountries = () => {
  const map = {};
  currencyCodes.data.forEach((entry) => {
    if (!entry.code || !entry.currency) return;

    let iso2 = null;
    if (!entry.code.startsWith("X")) {
      const prefix = entry.code.slice(0, 2);
      if (flagSupported.has(prefix)) iso2 = prefix;
    }
    if (!iso2) {
      for (const countryName of entry.countries || []) {
        const match = findIso2ForCountryName(countryName);
        if (match && flagSupported.has(match)) {
          iso2 = match;
          break;
        }
      }
    }
    if (iso2) {
      map[entry.code] = { country: iso2, countryName: iso2 === "EU" ? "Eurozone" : en[iso2] || iso2 };
    }
  });
  return map;
};

const CURRENCY_COUNTRIES = buildCurrencyCountries();

// An unmapped code (shouldn't happen for a real ISO 4217 currency, but
// cheap to guard) just renders without a flag/country name.
export const getCurrencyCountry = (code) => CURRENCY_COUNTRIES[code] || null;

export default CURRENCY_COUNTRIES;
